import { useMemo } from 'react'

import { GameId, supportedLanguages } from '@common'

import { safariDeviceType } from '../../../../utils/safariDeviceType'
import { useLocale } from '../../../locale/useLocale'
import { badAssetsBS } from '../../brawlstars/constants'
import { badAssetsCoC } from '../../clashofclans/contants'
import { badAssetsCR, animatedWebMs } from '../../clashroyale/constants'

type UseSmallerFontProps = {
  contentAmount: number
  size?: string
}

export const useSmallerFont = ({ contentAmount, size }: UseSmallerFontProps) => {
  return useMemo(() => {
    if (!size) {
      return contentAmount > Math.pow(10, 6)
    }
    return contentAmount > Math.pow(10, 6) && (size === 'size-bundle-sm' || size === 'size-product-page')
  }, [contentAmount, size])
}

type GetProductImageProps = {
  game: GameId
  productId: string
}

export const useProductImage = ({ game, productId }: GetProductImageProps) => {
  return useMemo(() => {
    const isBadAsset =
      game === 'clashofclans'
        ? badAssetsCoC.includes(productId)
        : game === 'clashroyale'
          ? badAssetsCR.includes(productId)
          : game === 'brawlstars'
            ? badAssetsBS.includes(productId)
            : false

    const isAnimated = game === 'clashroyale' && animatedWebMs.includes(productId)
    const useSafariAsset = safariDeviceType() === 'desktop' || safariDeviceType() === 'handheld'
    return {
      isAnimated,
      animatedSrc: `/images/${game}/assets/${productId}.webm`,
      imageSrc: isBadAsset
        ? `/images/${game}/assets/overrides/${productId}.png`
        : `/images/${game}/assets/${productId}.png`,
      safariAnimatedSrc: useSafariAsset && isAnimated ? `/images/${game}/assets/${productId}.webp` : undefined,
    }
  }, [game, productId])
}

export const useFormattedAmount = (amount: number) => {
  const locale = useLocale()
  return useMemo(() => {
    if (amount <= 0) {
      return null
    }

    const language = supportedLanguages[locale]
    if (language.rightToLeft) {
      const rtlAmount = new Intl.NumberFormat(language.locale).format(amount)
      return rtlAmount
    }

    // inserts a space before every group of three digits, starting from the end of the string
    return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  }, [amount, locale])
}
